import * as pathToRegexp from "path-to-regexp";

import {prefixPath} from "@pg-mono/prefix-path";
import {appendQueryString} from "@pg-mono/request";

import type {RegionRpId} from "./types/ids";

export const kmApiPath = prefixPath("/api/", {
    signup: "signup/",
    login: "login/",
    resetPassword: prefixPath("reset-password/", {
        base: "",
        token: ":token([\\w-]+)/"
    }),
    thirdPartyLogin: "third-party-login/",
    unsubscribe: prefixPath("unsubscribe/", {
        cpl: prefixPath("cpl/", {
            base: ""
        }),
        mailing: prefixPath("mailing/", {
            getLink: "get-link/",
            base: ":emailHash/:verificationHash/"
        }),
        hm: prefixPath("hm/", {
            base: ":login_token/"
        })
    }),
    applications: prefixPath("applications/", {
        application: "application/",
        financialApplication: prefixPath("financial-application/", {
            create: "create/",
            consents: "consents/"
        })
    }),
    homeMatch: prefixPath("homematch/", {
        configuration: "configuration/",
        resignation: "resignation/"
    }),
    properties: prefixPath("properties/", {
        property: prefixPath("property/", {
            detail: ":propertyId([0-9]+)/"
        }),
        neighborhood: "neighborhood/:rpId([0-9]+)/"
    }),
    user: prefixPath("user/", {
        changePassword: "change-password/",
        info: "info/",
        logout: "logout/",
        preferences: "preferences/",
        resendVerification: "resend-verification/",
        sendPhoneVerificationCode: "get-phone-verification-code/",
        verifyPhone: "verify-phone/"
    }),
    variables: "variables/",
    communication: prefixPath("communication/", {
        attachment: prefixPath("attachment/", {
            uuid: prefixPath(":uuid([\\w-]+)/", {
                getSignedUrl: "get-signed-url/"
            })
        }),
        discussionThread: prefixPath("discussion-thread/", {
            base: "",
            detail: ":threadId([0-9]+)/"
        }),
        message: prefixPath("message/", {
            base: "",
            listRead: "read/",
            detail: prefixPath(":threadId([0-9]+)/", {
                read: "read/",
                send: "send/"
            }),
            unreadCount: "unread-count/"
        })
    }),
    homematch: prefixPath("homematch/", {
        prelead: prefixPath("prelead/", {
            base: "",
            rejectedList: "rejected/",
            detail: prefixPath(":preleadId([0-9]+)/", {
                reject: "reject/",
                accept: "accept/",
                markShown: "mark_shown/"
            })
        })
    }),
    offerComparison: "offer-comparison/",
    survey: "survey/",
    regions: prefixPath("regions/", {
        base: "",
        priceStats: "price-stats/:rpId([0-9]+)/"
    }),
    favorites: prefixPath("favorites/", {
        comparison: "comparison/",
        comparisonSettings: "comparison-settings/",
        external: prefixPath("external/", {
            base: "",
            detail: ":favoriteId([0-9]+)/"
        }),
        list: "favorite/",
        neighborhood: prefixPath("neighborhood/", {
            external: "external/:externalPropertyId([0-9]+)/"
        })
    }),
    offers: prefixPath("offers/", {
        detail: ":rpId([0-9]+)/",
        lists: prefixPath("lists/", {
            detail: ":listType(favorites|inquiries|rejected)/"
        }),
        neighborhood: ":rpId([0-9]+)/neighborhood/"
    }),
    validators: prefixPath("validators/", {
        email: "verify-email/"
    })
});

export const kmApiLink = {
    signup: () => kmApiPath.signup,
    login: () => kmApiPath.login,
    resetPassword: {
        base: () => kmApiPath.resetPassword.base,
        token: createLink<{token: string}>(kmApiPath.resetPassword.token)
    },
    thirdPartyLogin: () => kmApiPath.thirdPartyLogin,
    unsubscribe: {
        cpl: {
            base: () => kmApiPath.unsubscribe.cpl.base
        },
        mailing: {
            base: createLink<{emailHash: string; verificationHash: string}>(kmApiPath.unsubscribe.mailing.base),
            getLink: kmApiPath.unsubscribe.mailing.getLink
        },
        hm: {
            base: createLink<{login_token: string}>(kmApiPath.unsubscribe.hm.base)
        }
    },
    properties: {
        property: {
            detail: createLink<{propertyId: number}>(kmApiPath.properties.property.detail)
        },
        neighborhood: createLink<{rpId: number}>(kmApiPath.properties.neighborhood)
    },
    applications: {
        application: () => kmApiPath.applications.application,
        financialApplication: {
            create: () => kmApiPath.applications.financialApplication.create,
            consents: () => kmApiPath.applications.financialApplication.consents
        }
    },
    user: {
        changePassword: () => kmApiPath.user.changePassword,
        info: () => kmApiPath.user.info,
        logout: () => kmApiPath.user.logout,
        preferences: () => kmApiPath.user.preferences,
        resendVerification: () => kmApiPath.user.resendVerification,
        sendPhoneVerificationCode: () => kmApiPath.user.sendPhoneVerificationCode,
        verifyPhone: () => kmApiPath.user.verifyPhone
    },
    homeMatch: {
        configuration: () => kmApiPath.homeMatch.configuration,
        resignation: () => kmApiPath.homeMatch.resignation
    },
    variables: () => kmApiPath.variables,
    communication: {
        attachment: {
            uuid: {
                getSignedUrl: createLink<{uuid: string}>(kmApiPath.communication.attachment.uuid.getSignedUrl)
            }
        },
        discussionThread: {
            list: () => kmApiPath.communication.discussionThread.base,
            detail: createLink<{threadId: number}>(kmApiPath.communication.discussionThread.detail)
        },
        message: {
            send: () => kmApiPath.communication.message.base,
            listRead: () => kmApiPath.communication.message.listRead,
            detail: {
                read: createLink<{threadId: number}>(kmApiPath.communication.message.detail.read),
                send: createLink<{threadId: number}>(kmApiPath.communication.message.detail.send)
            },
            unreadCount: () => kmApiPath.communication.message.unreadCount
        }
    },
    homematch: {
        prelead: {
            list: () => kmApiPath.homematch.prelead.base,
            rejectedList: () => kmApiPath.homematch.prelead.rejectedList,
            detail: {
                reject: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.reject),
                accept: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.accept),
                markShown: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.markShown)
            }
        }
    },
    offerComparison: createNoParamLink(kmApiPath.offerComparison),
    survey: () => kmApiPath.survey,
    regions: {
        base: createNoParamLinkWithScenario<IRegionScenarios>(kmApiPath.regions.base),
        priceStats: createLink<{rpId: RegionRpId}>(kmApiPath.regions.priceStats)
    },
    favorites: {
        comparison: createNoParamLink(kmApiPath.favorites.comparison),
        comparisonSettings: createNoParamLink(kmApiPath.favorites.comparisonSettings),
        external: {
            base: createNoParamLink(kmApiPath.favorites.external.base),
            detail: createLink<{favoriteId: number}>(kmApiPath.favorites.external.detail)
        },
        list: createNoParamLink(kmApiPath.favorites.list),
        neighborhood: {
            external: createLink<{externalPropertyId: number}>(kmApiPath.favorites.neighborhood.external)
        }
    },
    offers: {
        detail: createLink<{rpId: number}>(kmApiPath.offers.detail),
        lists: createLink<{listType: "favorites" | "inquiries" | "rejected"}>(kmApiPath.offers.lists.detail),
        neighborhood: createLink<{rpId: number}>(kmApiPath.offers.neighborhood)
    },
    validators: {
        email: createNoParamLink(kmApiPath.validators.email)
    }
};

export enum Scenario {
    REGION_DETAIL = "region-detail",
    REGION_LIST = "region-list",
    REGION_REPORT_PRICE = "region-report-price",
    SEARCH_REGION_DETAIL = "search-region-detail",
    SEARCH_REGION_PARENT = "search-region-parent",
    REGION_LIST_MAP = "region-list-map"
}

type IRegionScenarios =
    | Scenario.REGION_LIST
    | Scenario.SEARCH_REGION_DETAIL
    | Scenario.SEARCH_REGION_PARENT
    | Scenario.REGION_LIST_MAP
    | Scenario.REGION_REPORT_PRICE
    | Scenario.REGION_DETAIL;

function createLink<TParams = null>(apiPathElement: string) {
    const compiler = pathToRegexp.compile(apiPathElement);
    return (apiPathParams: TParams): string => compiler(apiPathParams as unknown as Record<string, string>);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createLinkWithScenario<TScenario, TParams>(apiPathElement: string) {
    type ParamsType = TParams | null;
    const compiler = pathToRegexp.compile(apiPathElement);
    return (scenario: TScenario, apiPathParams: ParamsType = null): string =>
        appendQueryString(compiler(apiPathParams as unknown as Record<string, string>), {s: scenario as unknown as string});
}

function createNoParamLinkWithScenario<TScenario>(apiPathElement: string) {
    return (scenario: TScenario): string => appendQueryString(apiPathElement, {s: scenario as unknown as string});
}

export function createNoParamLink(apiPathElement: string) {
    return () => apiPathElement;
}

export enum Scenario {}
import * as pathToRegexp from "path-to-regexp";

import {prefixPath} from "@pg-mono/prefix-path";
import {appendQueryString} from "@pg-mono/request";

import type {RegionRpId} from "./types/ids";

export const kmApiPath = prefixPath("/api/", {
    signup: "signup/",
    login: "login/",
    resetPassword: prefixPath("reset-password/", {
        base: "",
        token: ":token([\\w-]+)/"
    }),
    thirdPartyLogin: "third-party-login/",
    unsubscribe: prefixPath("unsubscribe/", {
        cpl: prefixPath("cpl/", {
            base: ""
        }),
        mailing: prefixPath("mailing/", {
            getLink: "get-link/",
            base: ":emailHash/:verificationHash/"
        }),
        hm: prefixPath("hm/", {
            base: ":login_token/"
        })
    }),
    applications: prefixPath("applications/", {
        application: "application/",
        financialApplication: prefixPath("financial-application/", {
            create: "create/",
            consents: "consents/"
        })
    }),
    homeMatch: prefixPath("homematch/", {
        configuration: "configuration/",
        resignation: "resignation/"
    }),
    properties: prefixPath("properties/", {
        property: prefixPath("property/", {
            detail: ":propertyId([0-9]+)/"
        }),
        neighborhood: "neighborhood/:rpId([0-9]+)/"
    }),
    user: prefixPath("user/", {
        changePassword: "change-password/",
        info: "info/",
        logout: "logout/",
        preferences: "preferences/",
        resendVerification: "resend-verification/",
        sendPhoneVerificationCode: "get-phone-verification-code/",
        verifyPhone: "verify-phone/"
    }),
    variables: "variables/",
    communication: prefixPath("communication/", {
        attachment: prefixPath("attachment/", {
            uuid: prefixPath(":uuid([\\w-]+)/", {
                getSignedUrl: "get-signed-url/"
            })
        }),
        discussionThread: prefixPath("discussion-thread/", {
            base: "",
            detail: ":threadId([0-9]+)/"
        }),
        message: prefixPath("message/", {
            base: "",
            listRead: "read/",
            detail: prefixPath(":threadId([0-9]+)/", {
                read: "read/",
                send: "send/"
            }),
            unreadCount: "unread-count/"
        })
    }),
    homematch: prefixPath("homematch/", {
        prelead: prefixPath("prelead/", {
            base: "",
            rejectedList: "rejected/",
            detail: prefixPath(":preleadId([0-9]+)/", {
                reject: "reject/",
                accept: "accept/",
                markShown: "mark_shown/"
            })
        })
    }),
    offerComparison: "offer-comparison/",
    survey: "survey/",
    regions: prefixPath("regions/", {
        base: "",
        priceStats: "price-stats/:rpId([0-9]+)/"
    }),
    favorites: prefixPath("favorites/", {
        comparison: "comparison/",
        comparisonSettings: "comparison-settings/",
        external: prefixPath("external/", {
            base: "",
            detail: ":favoriteId([0-9]+)/"
        }),
        list: "favorite/",
        neighborhood: prefixPath("neighborhood/", {
            external: "external/:externalPropertyId([0-9]+)/"
        })
    }),
    offers: prefixPath("offers/", {
        detail: ":rpId([0-9]+)/",
        lists: prefixPath("lists/", {
            detail: ":listType(favorites|inquiries|rejected)/"
        }),
        neighborhood: ":rpId([0-9]+)/neighborhood/"
    }),
    validators: prefixPath("validators/", {
        email: "verify-email/"
    })
});

export const kmApiLink = {
    signup: () => kmApiPath.signup,
    login: () => kmApiPath.login,
    resetPassword: {
        base: () => kmApiPath.resetPassword.base,
        token: createLink<{token: string}>(kmApiPath.resetPassword.token)
    },
    thirdPartyLogin: () => kmApiPath.thirdPartyLogin,
    unsubscribe: {
        cpl: {
            base: () => kmApiPath.unsubscribe.cpl.base
        },
        mailing: {
            base: createLink<{emailHash: string; verificationHash: string}>(kmApiPath.unsubscribe.mailing.base),
            getLink: kmApiPath.unsubscribe.mailing.getLink
        },
        hm: {
            base: createLink<{login_token: string}>(kmApiPath.unsubscribe.hm.base)
        }
    },
    properties: {
        property: {
            detail: createLink<{propertyId: number}>(kmApiPath.properties.property.detail)
        },
        neighborhood: createLink<{rpId: number}>(kmApiPath.properties.neighborhood)
    },
    applications: {
        application: () => kmApiPath.applications.application,
        financialApplication: {
            create: () => kmApiPath.applications.financialApplication.create,
            consents: () => kmApiPath.applications.financialApplication.consents
        }
    },
    user: {
        changePassword: () => kmApiPath.user.changePassword,
        info: () => kmApiPath.user.info,
        logout: () => kmApiPath.user.logout,
        preferences: () => kmApiPath.user.preferences,
        resendVerification: () => kmApiPath.user.resendVerification,
        sendPhoneVerificationCode: () => kmApiPath.user.sendPhoneVerificationCode,
        verifyPhone: () => kmApiPath.user.verifyPhone
    },
    homeMatch: {
        configuration: () => kmApiPath.homeMatch.configuration,
        resignation: () => kmApiPath.homeMatch.resignation
    },
    variables: () => kmApiPath.variables,
    communication: {
        attachment: {
            uuid: {
                getSignedUrl: createLink<{uuid: string}>(kmApiPath.communication.attachment.uuid.getSignedUrl)
            }
        },
        discussionThread: {
            list: () => kmApiPath.communication.discussionThread.base,
            detail: createLink<{threadId: number}>(kmApiPath.communication.discussionThread.detail)
        },
        message: {
            send: () => kmApiPath.communication.message.base,
            listRead: () => kmApiPath.communication.message.listRead,
            detail: {
                read: createLink<{threadId: number}>(kmApiPath.communication.message.detail.read),
                send: createLink<{threadId: number}>(kmApiPath.communication.message.detail.send)
            },
            unreadCount: () => kmApiPath.communication.message.unreadCount
        }
    },
    homematch: {
        prelead: {
            list: () => kmApiPath.homematch.prelead.base,
            rejectedList: () => kmApiPath.homematch.prelead.rejectedList,
            detail: {
                reject: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.reject),
                accept: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.accept),
                markShown: createLink<{preleadId: number}>(kmApiPath.homematch.prelead.detail.markShown)
            }
        }
    },
    offerComparison: createNoParamLink(kmApiPath.offerComparison),
    survey: () => kmApiPath.survey,
    regions: {
        base: createNoParamLinkWithScenario<IRegionScenarios>(kmApiPath.regions.base),
        priceStats: createLink<{rpId: RegionRpId}>(kmApiPath.regions.priceStats)
    },
    favorites: {
        comparison: createNoParamLink(kmApiPath.favorites.comparison),
        comparisonSettings: createNoParamLink(kmApiPath.favorites.comparisonSettings),
        external: {
            base: createNoParamLink(kmApiPath.favorites.external.base),
            detail: createLink<{favoriteId: number}>(kmApiPath.favorites.external.detail)
        },
        list: createNoParamLink(kmApiPath.favorites.list),
        neighborhood: {
            external: createLink<{externalPropertyId: number}>(kmApiPath.favorites.neighborhood.external)
        }
    },
    offers: {
        detail: createLink<{rpId: number}>(kmApiPath.offers.detail),
        lists: createLink<{listType: "favorites" | "inquiries" | "rejected"}>(kmApiPath.offers.lists.detail),
        neighborhood: createLink<{rpId: number}>(kmApiPath.offers.neighborhood)
    },
    validators: {
        email: createNoParamLink(kmApiPath.validators.email)
    }
};

export enum Scenario {
    REGION_DETAIL = "region-detail",
    REGION_LIST = "region-list",
    REGION_REPORT_PRICE = "region-report-price",
    SEARCH_REGION_DETAIL = "search-region-detail",
    SEARCH_REGION_PARENT = "search-region-parent",
    REGION_LIST_MAP = "region-list-map"
}

type IRegionScenarios =
    | Scenario.REGION_LIST
    | Scenario.SEARCH_REGION_DETAIL
    | Scenario.SEARCH_REGION_PARENT
    | Scenario.REGION_LIST_MAP
    | Scenario.REGION_REPORT_PRICE
    | Scenario.REGION_DETAIL;

function createLink<TParams = null>(apiPathElement: string) {
    const compiler = pathToRegexp.compile(apiPathElement);
    return (apiPathParams: TParams): string => compiler(apiPathParams as unknown as Record<string, string>);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createLinkWithScenario<TScenario, TParams>(apiPathElement: string) {
    type ParamsType = TParams | null;
    const compiler = pathToRegexp.compile(apiPathElement);
    return (scenario: TScenario, apiPathParams: ParamsType = null): string =>
        appendQueryString(compiler(apiPathParams as unknown as Record<string, string>), {s: scenario as unknown as string});
}

function createNoParamLinkWithScenario<TScenario>(apiPathElement: string) {
    return (scenario: TScenario): string => appendQueryString(apiPathElement, {s: scenario as unknown as string});
}

export function createNoParamLink(apiPathElement: string) {
    return () => apiPathElement;
}

export enum Scenario {}
